// PipeElement.jsx - Professional pipe rendering with 3D effect and flow animation
import React from 'react';

// Pipe style presets
export const PIPE_STYLES = {
  single: {
    label: 'Single',
    layers: 1,
    innerRatio: 0.6
  },
  double: {
    label: 'Double Wall',
    layers: 2,
    innerRatio: 0.5
  },
  insulated: {
    label: 'Insulated',
    layers: 3,
    innerRatio: 0.4
  }
};

export default function PipeElement({
  id,
  path,
  width = 20,
  color = '#3b82f6',
  style = 'single',
  flowEnabled = false,
  flowSpeed = 1,
  flowDirection = 'forward',
  flowColor = '#60a5fa',
  isSelected = false,
  darkMode = false,
  onClick
}) {
  const pipeStyle = PIPE_STYLES[style] || PIPE_STYLES.single;
  const gradientId = `pipe-gradient-${id}`;
  const highlightId = `pipe-highlight-${id}`;

  // Calculate layer widths
  const outerWidth = width;
  const middleWidth = width * 0.75;
  const innerWidth = width * pipeStyle.innerRatio;

  // Flow animation duration based on speed
  const flowDuration = 2 / flowSpeed;

  // Dash cycle for path-following flow animation
  const dashLength = innerWidth * 0.7;
  const gapLength = innerWidth+1;
  const dashCycle = dashLength + gapLength;

  // Colors for 3D gradient effect
  const darkColor = adjustColor(color, -30);
  const lightColor = adjustColor(color, 30);
  const highlightColor = adjustColor(color, 60);

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      {/* SVG Definitions */}
      <defs>
        {/* 3D Gradient for pipe body */}
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={lightColor} />
          <stop offset="30%" stopColor={color} />
          <stop offset="70%" stopColor={color} />
          <stop offset="100%" stopColor={darkColor} />
        </linearGradient>

        {/* Highlight gradient */}
        <linearGradient id={highlightId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={highlightColor} stopOpacity="0.5" />
          <stop offset="50%" stopColor={highlightColor} stopOpacity="0" />
        </linearGradient>

        {/* CSS keyframe animation for flow — direction-aware */}
        {flowEnabled && (
          <style>{`
            @keyframes flow-fwd-${id} {
              from { stroke-dashoffset: ${dashCycle}; }
              to   { stroke-dashoffset: 0; }
            }
            @keyframes flow-rev-${id} {
              from { stroke-dashoffset: 0; }
              to   { stroke-dashoffset: ${dashCycle}; }
            }
          `}</style>
        )}
      </defs>

      {/* Shadow layer */}
      <path
        d={path}
        stroke="rgba(0,0,0,0.2)"
        strokeWidth={outerWidth + 4}
        fill="none"
        strokeLinecap="butt"
        strokeLinejoin="miter"
        transform="translate(2, 2)"
      />

      {/* Outer pipe layer (for double/insulated styles) */}
      {pipeStyle.layers >= 2 && (
        <path
          d={path}
          stroke={darkColor}
          strokeWidth={outerWidth}
          fill="none"
          strokeLinecap="butt"
          strokeLinejoin="miter"
        />
      )}

      {/* Middle layer (for insulated style) */}
      {pipeStyle.layers >= 3 && (
        <path
          d={path}
          stroke={darkMode ? '#374151' : '#d1d5db'}
          strokeWidth={middleWidth}
          fill="none"
          strokeLinecap="butt"
          strokeLinejoin="miter"
        />
      )}

      {/* Main pipe body with gradient */}
      <path
        d={path}
        stroke={`url(#${gradientId})`}
        strokeWidth={pipeStyle.layers >= 2 ? middleWidth : outerWidth}
        fill="none"
        strokeLinecap="butt"
        strokeLinejoin="miter"
      />

      {/* Inner pipe (flow channel) */}
      <path
        d={path}
        stroke={darkMode ? '#1e293b' : '#e5e7eb'}
        strokeWidth={innerWidth}
        fill="none"
        strokeLinecap="butt"
        strokeLinejoin="miter"
      />

      {/* Flow animation — CSS animation follows pipe path direction */}
      {flowEnabled && (
        <path
          d={path}
          stroke={flowColor}
          strokeWidth={innerWidth - 2}
          fill="none"
          strokeLinecap="butt"
          strokeLinejoin="miter"
          strokeDasharray={`${dashLength} ${gapLength}`}
          opacity="0.85"
          style={{
            animation: `${flowDirection === 'reverse' ? `flow-rev-${id}` : `flow-fwd-${id}`} ${flowDuration}s linear infinite`
          }}
        />
      )}

      {/* Highlight */}
      <path
        d={path}
        stroke={`url(#${highlightId})`}
        strokeWidth={pipeStyle.layers >= 2 ? middleWidth * 0.8 : outerWidth * 0.8}
        fill="none"
        strokeLinecap="butt"
        strokeLinejoin="miter"
        opacity="0.5"
      />

      {/* Selection indicator */}
      {isSelected && (
        <path
          d={path}
          stroke={darkMode ? '#60a5fa' : '#3b82f6'}
          strokeWidth={outerWidth + 8}
          fill="none"
          strokeLinecap="butt"
          strokeLinejoin="miter"
          strokeDasharray="8 4"
          opacity="0.5"
        />
      )}
    </g>
  );
}

// Helper function to adjust color brightness
function adjustColor(color, amount) {
  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const num = parseInt(hex, 16);
    const r = Math.min(255, Math.max(0, (num >> 16) + amount));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
    const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
    return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`;
  }
  return color;
}

// Helper to generate pipe path from control points — always straight segments (polyline)
export function generatePipePath(startX, startY, endX, endY, controlPoints = []) {
  let path = `M ${startX} ${startY}`;
  (controlPoints || []).forEach(cp => { path += ` L ${cp.x} ${cp.y}`; });
  path += ` L ${endX} ${endY}`;
  return path;
}

// Default pipe configuration
export const DEFAULT_PIPE = {
  width: 20,
  color: '#3b82f6',
  style: 'single',
  flowEnabled: false,
  flowSpeed: 1,
  flowDirection: 'forward',
  flowColor: '#60a5fa'
};
